/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

class MultiCardCarousel {
    constructor() {
        this.currentSlide = 0;
        this.autoSlideInterval = null;
        this.progressInterval = null;
        this.slideInterval = 4000; // 4 seconds
        this.isTransitioning = false;
        this.cardsPerSlide = this.getCardsPerSlide();

        this.init();
        this.handleResize();
    }

    init() {
        // Multiple detection methods for better reliability
        this.waitForReviews();
        this.observeDOM();
    }

    observeDOM() {
        // Use MutationObserver to watch for dynamic content
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // Check if review content was added
                    for (let node of mutation.addedNodes) {
                        if (node.nodeType === 1) { // Element node
                            const hasReviewWidget = node.classList?.contains('stamped-wall-photos-wrapper') ||
                                node.querySelector?.('.stamped-wall-photos-wrapper');
                            const hasReviewCards = node.classList?.contains('stamped-photo-wrapper') ||
                                node.querySelector?.('.stamped-photo-wrapper');

                            if (hasReviewWidget || hasReviewCards) {
                                // Give it a moment to fully load
                                setTimeout(() => {
                                    const widget = document.querySelector('.stamped-wall-photos-wrapper');
                                    if (widget && !widget.classList.contains('carousel-active')) {
                                        const reviews = widget.querySelectorAll('.stamped-photo-wrapper');
                                        if (reviews.length > 0) {
                                            observer.disconnect();
                                            this.setupCarousel();
                                        }
                                    }
                                }, 500);
                                break;
                            }
                        }
                    }
                }
            });
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Stop observing after 20 seconds
        setTimeout(() => {
            observer.disconnect();
        }, 20000);
    }

    waitForReviews() {
        let attempts = 0;
        const maxAttempts = 150; // 15 seconds with 100ms intervals

        const checkReviews = setInterval(() => {
            attempts++;

            const widget = document.querySelector('.stamped-wall-photos-wrapper');

            if (!widget) {
                if (attempts >= maxAttempts) {
                    clearInterval(checkReviews);
                    console.log('Review widget not found after 15 seconds');
                }
                return;
            }

            // Check if already converted to carousel
            if (widget.classList.contains('carousel-active')) {
                clearInterval(checkReviews);
                return;
            }

            // Look for actual review content
            const reviews = widget.querySelectorAll('.stamped-photo-wrapper');
            const hasContent = reviews.length > 0;

            // Additional check: make sure reviews have actual content
            let hasRealContent = false;
            if (hasContent) {
                for (let review of reviews) {
                    const hasImage = review.querySelector('img[src]');
                    const hasAuthor = review.querySelector('.stamped-reviews-author');
                    const hasMessage = review.querySelector('.stamped-reviews-message');

                    if (hasImage && hasAuthor && hasMessage) {
                        hasRealContent = true;
                        break;
                    }
                }
            }

            if (hasRealContent) {
                clearInterval(checkReviews);
                console.log(`Reviews found after ${attempts * 100}ms`);
                this.setupCarousel();
            } else if (attempts >= maxAttempts) {
                clearInterval(checkReviews);
                console.log('Timeout: Reviews not fully loaded after 15 seconds');
                // Try to setup anyway if widget exists
                if (widget) {
                    this.setupCarousel();
                }
            }
        }, 100);
    }

    getCardsPerSlide() {
        const width = window.innerWidth;
        if (width >= 1024) return 5; // Desktop
        if (width >= 768) return 3;  // Tablet
        return 2; // Mobile
    }

    setupCarousel() {
        const widget = document.querySelector('.stamped-wall-photos-wrapper');
        if (!widget) {
            console.log('No review widget found');
            return;
        }

        if (widget.classList.contains('carousel-active')) {
            console.log('Carousel already active');
            return;
        }

        const reviews = Array.from(widget.querySelectorAll('.stamped-photo-wrapper'));
        if (reviews.length === 0) {
            console.log('No review cards found');
            return;
        }

        console.log(`Setting up carousel with ${reviews.length} reviews`);

        // Transform the widget into carousel
        widget.classList.add('carousel-active');

        // Create track container
        const track = document.createElement('div');
        track.className = 'carousel-track';

        // Move all reviews into the track
        reviews.forEach(review => {
            track.appendChild(review);
        });

        // Clear widget and add track
        widget.innerHTML = '';
        widget.appendChild(track);

        // Add navigation elements
        this.addNavigation(widget);
        this.addDots(widget, reviews.length);
        this.addProgressBar(widget);

        // Set up event listeners
        this.attachEventListeners();

        // Start auto-slide
        this.startAutoSlide();
    }

    addNavigation(widget) {
        const prevBtn = document.createElement('button');
        prevBtn.className = 'carousel-nav prev';
        prevBtn.innerHTML = '‹';
        prevBtn.addEventListener('click', () => this.prevSlide());

        const nextBtn = document.createElement('button');
        nextBtn.className = 'carousel-nav next';
        nextBtn.innerHTML = '›';
        nextBtn.addEventListener('click', () => this.nextSlide());

        widget.appendChild(prevBtn);
        widget.appendChild(nextBtn);
    }

    addDots(widget, totalReviews) {
        const maxSlides = Math.ceil(totalReviews / this.cardsPerSlide);
        const dotsContainer = document.createElement('div');
        dotsContainer.className = 'carousel-dots';

        for (let i = 0; i < maxSlides; i++) {
            const dot = document.createElement('div');
            dot.className = `carousel-dot ${i === 0 ? 'active' : ''}`;
            dot.addEventListener('click', () => this.goToSlide(i));
            dotsContainer.appendChild(dot);
        }

        widget.appendChild(dotsContainer);
    }

    addProgressBar(widget) {
        const progressBar = document.createElement('div');
        progressBar.className = 'carousel-progress';
        widget.appendChild(progressBar);
    }

    attachEventListeners() {
        const widget = document.querySelector('.stamped-wall-photos-wrapper.carousel-active');
        if (widget) {
            widget.addEventListener('mouseenter', () => this.pauseAutoSlide());
            widget.addEventListener('mouseleave', () => this.startAutoSlide());
        }

        // Handle resize
        window.addEventListener('resize', () => this.handleResize());
    }

    handleResize() {
        const newCardsPerSlide = this.getCardsPerSlide();
        if (newCardsPerSlide !== this.cardsPerSlide) {
            this.cardsPerSlide = newCardsPerSlide;
            this.updateDotsForResize();
            this.goToSlide(0); // Reset to first slide
        }
    }

    updateDotsForResize() {
        const widget = document.querySelector('.stamped-wall-photos-wrapper.carousel-active');
        const track = widget ? widget.querySelector('.carousel-track') : null;
        if (!track) return;

        const totalReviews = track.children.length;
        const maxSlides = Math.ceil(totalReviews / this.cardsPerSlide);

        const dotsContainer = widget.querySelector('.carousel-dots');
        if (dotsContainer) {
            dotsContainer.innerHTML = '';

            for (let i = 0; i < maxSlides; i++) {
                const dot = document.createElement('div');
                dot.className = `carousel-dot ${i === 0 ? 'active' : ''}`;
                dot.addEventListener('click', () => this.goToSlide(i));
                dotsContainer.appendChild(dot);
            }
        }
    }

    goToSlide(slideIndex) {
        if (this.isTransitioning) return;

        const track = document.querySelector('.carousel-track');
        const dots = document.querySelectorAll('.carousel-dot');

        if (!track || dots.length === 0) return;

        const totalReviews = track.children.length;
        const maxSlides = Math.ceil(totalReviews / this.cardsPerSlide);

        // Bound check
        if (slideIndex < 0 || slideIndex >= maxSlides) return;

        this.isTransitioning = true;
        this.currentSlide = slideIndex;

        // Calculate transform
        const slideWidth = 100 / this.cardsPerSlide;
        const translateX = slideIndex * slideWidth * this.cardsPerSlide;

        track.style.transform = `translateX(-${translateX}%)`;

        // Update dots
        dots.forEach((dot, i) => {
            dot.classList.toggle('active', i === slideIndex);
        });

        // Reset transition flag
        setTimeout(() => {
            this.isTransitioning = false;
        }, 500);

        this.resetProgress();
    }

    nextSlide() {
        const track = document.querySelector('.carousel-track');
        if (!track) return;

        const totalReviews = track.children.length;
        const maxSlides = Math.ceil(totalReviews / this.cardsPerSlide);

        this.goToSlide((this.currentSlide + 1) % maxSlides);
    }

    prevSlide() {
        const track = document.querySelector('.carousel-track');
        if (!track) return;

        const totalReviews = track.children.length;
        const maxSlides = Math.ceil(totalReviews / this.cardsPerSlide);

        this.goToSlide((this.currentSlide - 1 + maxSlides) % maxSlides);
    }

    startAutoSlide() {
        this.pauseAutoSlide();
        this.startProgress();

        this.autoSlideInterval = setInterval(() => {
            this.nextSlide();
        }, this.slideInterval);
    }

    pauseAutoSlide() {
        if (this.autoSlideInterval) {
            clearInterval(this.autoSlideInterval);
            this.autoSlideInterval = null;
        }
        this.pauseProgress();
    }

    startProgress() {
        this.resetProgress();
        let width = 0;
        const increment = 100 / (this.slideInterval / 50);

        this.progressInterval = setInterval(() => {
            width += increment;
            const progressBar = document.querySelector('.carousel-progress');
            if (progressBar) {
                progressBar.style.width = Math.min(width, 100) + '%';
            }
        }, 50);
    }

    pauseProgress() {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    }

    resetProgress() {
        this.pauseProgress();
        const progressBar = document.querySelector('.carousel-progress');
        if (progressBar) {
            progressBar.style.width = '0%';
        }
    }
}

// Initialize with robust polling
function initCarousel() {
    window.reviewCarousel = new MultiCardCarousel();
}

// Start immediately and keep checking
initCarousel();

// Manual trigger function (backup)
window.initReviewCarousel = function() {
    if (window.reviewCarousel) {
        window.reviewCarousel.setupCarousel();
    } else {
        initCarousel();
    }
};